import { useCallback, useMemo, useRef } from "react";
import {
  CreatableFieldDocumentType,
  CreatableFieldsQuery,
  useCreatableFieldsQuery,
} from "generated/general";
import { createGraphQLClientWithMiddleware } from "services/graphqlClient";
import {
  isTrailerType,
  VEHICLE_TYPE_CREATABLE_FIELD_NAME,
} from "utils/Logistic/vehicleType";

export interface VehicleTypeOption {
  label: string;
  value: string;
  disabled?: boolean;
}

interface Options {
  excludeTrailer?: boolean;
}

export const useVehicleTypeOptions = ({ excludeTrailer = false }: Options = {}) => {
  const graphQLClient = createGraphQLClientWithMiddleware("general");

  const { data, isSuccess, isLoading } =
    useCreatableFieldsQuery<CreatableFieldsQuery>(graphQLClient, {
      findManyInput: {
        document_type: "vehicle" as CreatableFieldDocumentType,
        creatable_fields_name: VEHICLE_TYPE_CREATABLE_FIELD_NAME,
      },
    });

  const options: VehicleTypeOption[] = useMemo(() => {
    const rows = data?.CreatableFields ?? [];
    return rows
      .filter((row) => (excludeTrailer ? !isTrailerType(row.name) : true))
      .map((row) => ({ label: row.name, value: row.name, disabled: false }));
  }, [data, excludeTrailer]);

  return { options, isSuccess, isLoading };
};

/**
 * Lazy fetcher for ag-grid Set Filter `values: async (params) => {}`.
 * Master rows are read from CreatableField at filter-open time so the list
 * reflects any user-added vehicle types.
 */
export const useVehicleTypeValues = () => {
  // Memoize the GraphQL client — recreating it every render made
  // `refetch` and therefore `fetchValues` new each render, which cascaded
  // into ag-grid columnDefs useEffects and blew the update depth.
  const graphQLClient = useMemo(
    () => createGraphQLClientWithMiddleware("general"),
    [],
  );

  const { refetch } = useCreatableFieldsQuery<CreatableFieldsQuery>(
    graphQLClient,
    {
      findManyInput: {
        document_type: "vehicle" as CreatableFieldDocumentType,
        creatable_fields_name: VEHICLE_TYPE_CREATABLE_FIELD_NAME,
      },
    },
    { enabled: false },
  );

  // Keep the latest refetch in a ref so `fetchValues` itself stays stable
  // even if a future refactor makes refetch unstable.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  const fetchValues = useCallback(
    async (opts?: { excludeTrailer?: boolean }) => {
      const { data } = await refetchRef.current();
      const rows = data?.CreatableFields ?? [];
      return rows
        .map((row) => row.name)
        .filter((name) =>
          opts?.excludeTrailer ? !isTrailerType(name) : true,
        );
    },
    [],
  );

  return { fetchValues };
};
